import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, DollarSign, Shield, BarChart3, Calendar, Building2, Award, AlertCircle, CheckCircle } from 'lucide-react';

import { useParams, useNavigate, useLocation } from 'react-router-dom';

const ProductComparison = ({ selectedProducts: propProducts, onBackToScanner }) => {
  const { fundIds } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // קח מוצרים מ-state או מ-props
  const [selectedProducts] = useState(
    location.state?.selectedProducts || propProducts || []
  );
  useEffect(() => {
  // גלילה חלקה לראש העמוד
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, []);
  const backToScanner = () => {
    if (onBackToScanner) {
      onBackToScanner();
    } else {
      navigate('/route-scanner');
    }
  };
  // אם אין מוצרים ולא טוען
  if (!selectedProducts || selectedProducts.length === 0) {
  // הסר את התנאי של loading לחלוטין
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-600 mb-4">אין מוצרים להשוואה</h2>
        <button
          onClick={backToScanner}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          חזור לסורק המסלולים
        </button>
      </div>
    </div>
  );
}

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'לא זמין';
    return new Intl.NumberFormat('he-IL').format(num);
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return 'לא זמין';
    return `${num.toFixed(2)}%`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'לא זמין';
    if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(1)}B ₪`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M ₪`;
    } else {
      return `${formatNumber(amount)} ₪`;
    }
  };

  const getBestPerformer = (field) => {
    let bestValue = -Infinity;
    let bestIndex = -1;
    
    selectedProducts.forEach((product, index) => {
      const value = getNestedValue(product, field);
      if (value !== null && value !== undefined && value > bestValue) {
        bestValue = value;
        bestIndex = index;
      }
    });
    
    return bestIndex;
  };

  const getWorstPerformer = (field) => {
    let worstValue = Infinity;
    let worstIndex = -1;
    
    selectedProducts.forEach((product, index) => {
      const value = getNestedValue(product, field);
      if (value !== null && value !== undefined && value < worstValue) {
        worstValue = value;
        worstIndex = index;
      }
    });
    
    return worstIndex;
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const getPerformanceColor = (value, field, index) => {
    if (value === null || value === undefined) return 'text-gray-500';
    
    const bestIndex = getBestPerformer(field);
    const worstIndex = getWorstPerformer(field);
    
    if (index === bestIndex) return 'text-green-600 font-bold';
    if (index === worstIndex) return 'text-red-600';
    return 'text-gray-800';
  };

  const getPerformanceIcon = (value, field, index) => {
    if (value === null || value === undefined) return null;
    
    const bestIndex = getBestPerformer(field);
    const worstIndex = getWorstPerformer(field);
    
    // if (index === bestIndex) return <Award className="h-4 w-4 text-green-600 inline mr-1" />;
    // if (index === worstIndex) return <AlertCircle className="h-4 w-4 text-red-600 inline mr-1" />;
    return null;
  };

  const comparisonRows = [
    {
      category: 'מידע בסיסי',
      icon: Building2,
      rows: [
          { label: 'מזהה קרן', field: 'id', type: 'text' }, 
        { label: 'שם המוצר', field: 'name', type: 'text' },
        { label: 'חברה מנהלת', field: 'company', type: 'text' },
        { label: 'סיווג', field: 'classification', type: 'text' },
        { label: 'תאריך הקמה', field: 'fundInfo.inceptionDate', type: 'text' },
        { label: 'סטטוס פעילות', field: 'activityStatus', type: 'status' },
      ]
    },
{
  category: 'תשואות (%)',
  icon: TrendingUp,
  rows: [
    { label: 'חודש אחרון', field: 'returns.month1', type: 'percent' },
    { label: 'מתחילת השנה', field: 'returns.ytd', type: 'percent' },
    { label: 'שנה אחרונה', field: 'returns.year1', type: 'percent' },
    { label: 'שנתיים', field: 'returns.year2', type: 'percent' },
    { label: '3 שנים', field: 'returns.year3', type: 'percent' }, // 🆕
    { label: '4 שנים', field: 'returns.year4', type: 'percent' },
    { label: '5 שנים', field: 'returns.year5', type: 'percent' }, // 🆕
    { label: 'ממוצע 2 שנים', field: 'returns.avgAnnual2Years', type: 'percent' },
    { label: 'ממוצע 3 שנים', field: 'returns.avgAnnual3Years', type: 'percent' }, // 🆕
    { label: 'ממוצע 4 שנים', field: 'returns.avgAnnual4Years', type: 'percent' },
    { label: 'ממוצע 5 שנים', field: 'returns.avgAnnual5Years', type: 'percent' }, // 🆕
  ]
},
    {
      category: 'עלויות (%)',
      icon: DollarSign,
      rows: [
        { label: 'דמי ניהול', field: 'fees.management', type: 'percent-reverse' },
        { label: 'דמי הפקדה', field: 'fees.deposit', type: 'percent-reverse' },
      ]
    },
    {
      category: 'סיכון וביצועים',
      icon: Shield,
      rows: [
      {label:'שם רמת הסיכון', field:'riskLevel.name', type:'text'},
     { label: 'רמת סיכון', field: 'riskLevel.level', type: 'risk' },
        { label: 'סטיית תקן', field: 'performance.standardDeviation', type: 'number' },
        { label: 'יחס שארפ', field: 'performance.sharpeRatio', type: 'number' },
        { label: 'אלפא', field: 'performance.alpha', type: 'number' },
      ]
    },
    {
      category: 'נכסים ופעילות',
      icon: BarChart3,
      rows: [
        { label: 'סך נכסים', field: 'totalAssets', type: 'currency' },
        { label: 'הפקדות חודשיות', field: 'deposits', type: 'currency-small' },
        { label: 'משיכות חודשיות', field: 'withdrawals', type: 'currency-small' },
        { label: 'הפקדות נטו', field: 'netMonthlyDeposits', type: 'currency-small' },
      ]
    },
    {
      category: 'חשיפות השקעה (%)',
      icon: BarChart3,
      rows: [
        { label: 'חשיפה למניות', field: 'exposures.stocks', type: 'percent' },
        { label: 'חשיפה לחו"ל', field: 'exposures.foreign', type: 'percent' },
      {label:"חשיפה לארץ",field:"exposures.local",type:"percent"},
        { label: 'חשיפה למטבע', field: 'exposures.currency', type: 'percent' },
      ]
    }
  ];

const renderValue = (product, row, index) => {
  let value = getNestedValue(product, row.field);
  
  // חישוב מיוחד לחשיפה לארץ
  if (row.field === 'exposures.local') {
    const foreignExposure = getNestedValue(product, 'exposures.foreign') || 0;
    value = Math.max(0, 100 - foreignExposure); // מינימום 0
  }
  
  let displayValue = 'לא זמין';
  let colorClass = 'text-gray-500';
  
  if (value !== null && value !== undefined) {
    switch (row.type) {
      case 'text':
        displayValue = value;
        colorClass = 'text-gray-800'; // צבע קבוע, ללא דירוג
        break;
      case 'status':
        displayValue = value;
        colorClass = product.isActive ? 'text-green-600' : 'text-red-600';
        break;
      case 'percent':
        displayValue = formatPercent(value);
        colorClass = getPerformanceColor(value, row.field, index);
        break;
      case 'percent-reverse':
        displayValue = formatPercent(value);
        colorClass = getPerformanceColor(-value, row.field, index); // הפוך לעלויות
        break;
      case 'number':
        displayValue = value.toFixed(2);
        colorClass = getPerformanceColor(value, row.field, index);
        break;
      case 'currency':
        displayValue = formatCurrency(value);
        colorClass = getPerformanceColor(value, row.field, index);
        break;
      case 'currency-small':
        displayValue = `${formatNumber(value)}M ₪`;
        colorClass = getPerformanceColor(value, row.field, index);
        break;
      case 'risk':
        displayValue = value;
        colorClass = 'text-gray-800';
        break;
      default:
        displayValue = value;
        colorClass = 'text-gray-800';
    }
  }

  return (
    <td key={index} className={`px-4 py-3 text-sm ${colorClass} text-center border-l border-gray-200`}>
      {row.type !== 'text' ? getPerformanceIcon(value, row.field, index) : null}
      {displayValue}
    </td>
  );
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        
        {/* כותרת וחזרה */}
        <div className="flex items-center justify-between mb-8">
       <button
  onClick={backToScanner}  // ✅ נכון
  className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors"
>
            <ArrowLeft className="h-5 w-5" />
            חזור לסורק המסלולים
          </button>
          
          <h1 className="text-3xl font-bold text-gray-800">
            השוואת {selectedProducts.length} מוצרים
          </h1>
          
          <div className="w-32"></div> {/* ספייסר לאיזון */}
        </div>

        {/* סיכום מהיר */}
     <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
  <h2 className="text-xl font-bold text-gray-800 mb-4">סיכום מהיר</h2>
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"> {/* שינוי ל-4 עמודות */}
    <div className="text-center p-4 bg-green-50 rounded-lg">
      <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
      <h3 className="font-bold text-green-800">תשואה הטובה ביותר (שנה)</h3>
      <p className="text-green-600">
        {(() => {
          const bestIndex = getBestPerformer('returns.year1');
          const bestProduct = selectedProducts[bestIndex];
          return bestProduct ? `${bestProduct.name}: ${formatPercent(bestProduct.returns?.year1)}` : 'לא זמין';
        })()}
      </p>
    </div>
    
    {/* 🆕 תשואה 5 שנים */}
    <div className="text-center p-4 bg-indigo-50 rounded-lg">
      <TrendingUp className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
      <h3 className="font-bold text-indigo-800">תשואה הטובה ביותר (5 שנים)</h3>
      <p className="text-indigo-600">
        {(() => {
          const bestIndex = getBestPerformer('returns.year5');
          const bestProduct = selectedProducts[bestIndex];
          return bestProduct ? `${bestProduct.name}: ${formatPercent(bestProduct.returns?.year5)}` : 'לא זמין';
        })()}
      </p>
    </div>
    
    <div className="text-center p-4 bg-blue-50 rounded-lg">
      <DollarSign className="h-8 w-8 text-blue-600 mx-auto mb-2" />
      <h3 className="font-bold text-blue-800">עלויות הנמוכות ביותר</h3>
      <p className="text-blue-600">
        {(() => {
          const bestIndex = getWorstPerformer('fees.management');
          const bestProduct = selectedProducts[bestIndex];
          return bestProduct ? `${bestProduct.name}: ${formatPercent(bestProduct.fees?.management)}` : 'לא זמין';
        })()}
      </p>
    </div>
    
    <div className="text-center p-4 bg-purple-50 rounded-lg">
      <BarChart3 className="h-8 w-8 text-purple-600 mx-auto mb-2" />
      <h3 className="font-bold text-purple-800">הקופה הגדולה ביותר</h3>
      <p className="text-purple-600">
        {(() => {
          const bestIndex = getBestPerformer('totalAssets');
          const bestProduct = selectedProducts[bestIndex];
          return bestProduct ? `${bestProduct.name}: ${formatCurrency(bestProduct.totalAssets)}` : 'לא זמין';
        })()}
      </p>
    </div>
  </div>
</div>

        {/* טבלת השוואה מפורטת */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-4 text-right text-sm font-bold text-gray-700 border-l border-gray-200">
                    פרמטר
                  </th>
        {selectedProducts.map((product, index) => (
  <th key={product.id} className="px-4 py-4 text-center text-sm font-bold text-gray-700 border-l border-gray-200 min-w-48">
    <div className="space-y-1">
      <div className="font-bold text-gray-800">{product.name}</div>
      <div className="text-xs text-gray-500">{product.company}</div>
      <div className="text-xs text-blue-600 font-mono">מספר באוצר: {product.id}</div> {/* 🆕 הוספת מזהה */}
      <div className={`inline-block px-2 py-1 rounded-full text-xs ${
        product.isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-red-100 text-red-800'
      }`}>
        {product.activityStatus}
      </div>
    </div>
  </th>
))}
                </tr>
              </thead>
              
              <tbody>
                {comparisonRows.map((category) => (
                  <React.Fragment key={category.category}>
                    <tr className="bg-gray-100">
                      <td colSpan={selectedProducts.length + 1} className="px-4 py-3 font-bold text-gray-800">
                        <div className="flex items-center gap-2">
                          <category.icon className="h-5 w-5" />
                          {category.category}
                        </div>
                      </td>
                    </tr>
                    
                    {category.rows.map((row, rowIndex) => (
                      <tr key={`${category.category}-${rowIndex}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-700 border-l border-gray-200">
                          {row.label}
                        </td>
                        {selectedProducts.map((product, index) => 
                          renderValue(product, row, index)
                        )}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* הערות חשובות */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-amber-800 mb-2">הערות חשובות:</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• התשואות מבוססות על נתונים היסטוריים ואינן מבטיחות תשואה עתידית</li>
                <li>• יש לבחון את התאמת המוצר לפרופיל הסיכון האישי</li>
                <li>• מומלץ להתייעץ עם יועץ פיננסי לפני קבלת החלטות השקעה</li>
                <li>• הנתונים מעודכנים למועד: {selectedProducts[0]?.reportPeriod || 'לא זמין'}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductComparison;